/* eslint-disable formatjs/no-literal-string-in-jsx */
import { Box, Text } from '@atlaskit/primitives';
import { type DatabaseField, DatabaseFieldType } from '@databases/types/database';
import React, { type FunctionComponent } from 'react';

import AttachmentFieldSettings from './AttachmentFieldSettings';
import EntryLinkFieldSettings from './EntryLinkFieldSettings';
import HyperlinkFieldSettings from './HyperlinkFieldSettings';
import JiraDetailsFieldSettings from './JiraDetailsFieldSettings';
import JiraLinkFieldSettings from './JiraLinkFieldSettings';
import UserFieldSettings from './user/UserFieldSettings.tsx';

const unreachable = (fieldType: never): never => {
  throw new Error(`Unsupported field settings menu type: ${fieldType}`);
};

const FieldSettingsPlaceholder: FunctionComponent<{
  fieldType: DatabaseFieldType;
}> = ({ fieldType }) => {
  return (
    <Box paddingBlock="space.100">
      <Text>
        {fieldType}
        {
          // temporary placeholder
        } field settings placeholder
      </Text>
    </Box>
  );
};

export const getFieldSettingsOptionsByType = (
  contentId: string,
  field: DatabaseField<DatabaseFieldType>
): React.ReactElement | null => {
  switch (field.type) {
    case DatabaseFieldType.ATTACHMENT:
      return (
        <AttachmentFieldSettings
          contentId={contentId}
          field={field as DatabaseField<DatabaseFieldType.ATTACHMENT>}
        />
      );

    case DatabaseFieldType.BACKLINK:
      return <FieldSettingsPlaceholder fieldType={field.type} />;

    case DatabaseFieldType.DATE:
      return <FieldSettingsPlaceholder fieldType={field.type} />;

    case DatabaseFieldType.ENTRY_DETAILS:
      return <FieldSettingsPlaceholder fieldType={field.type} />;

    case DatabaseFieldType.ENTRY_LINK:
      return (
        <Box paddingBlockEnd="space.200">
          <EntryLinkFieldSettings
            contentId={contentId}
            field={field as DatabaseField<DatabaseFieldType.ENTRY_LINK>}
          />
        </Box>
      );

    case DatabaseFieldType.HYPERLINK:
      return (
        <HyperlinkFieldSettings
          contentId={contentId}
          field={field as DatabaseField<DatabaseFieldType.HYPERLINK>}
        />
      );

    case DatabaseFieldType.JIRA_ISSUE:
      return (
        <Box paddingBlockEnd="space.200">
          <JiraLinkFieldSettings
            contentId={contentId}
            field={field as DatabaseField<DatabaseFieldType.JIRA_ISSUE>}
          />
        </Box>
      );

    case DatabaseFieldType.JIRA_ISSUE_DETAILS:
      return (
        <Box paddingBlockEnd="space.025">
          <JiraDetailsFieldSettings
            contentId={contentId}
            field={field as DatabaseField<DatabaseFieldType.JIRA_ISSUE_DETAILS>}
          />
        </Box>
      );

    case DatabaseFieldType.NUMBER:
      return <FieldSettingsPlaceholder fieldType={field.type} />;

    case DatabaseFieldType.PAGE_DETAILS:
      return <FieldSettingsPlaceholder fieldType={field.type} />;

    case DatabaseFieldType.PAGE_LABELS:
      return <FieldSettingsPlaceholder fieldType={field.type} />;

    case DatabaseFieldType.PAGE_LINK:
      return <FieldSettingsPlaceholder fieldType={field.type} />;

    case DatabaseFieldType.PAGE_STATUS:
      return <FieldSettingsPlaceholder fieldType={field.type} />;

    case DatabaseFieldType.SELECT:
      return <FieldSettingsPlaceholder fieldType={field.type} />;

    case DatabaseFieldType.USER:
      return (
        <UserFieldSettings
          contentId={contentId}
          field={field as DatabaseField<DatabaseFieldType.USER>}
        />
      );

    case DatabaseFieldType.TEXT:
    case DatabaseFieldType.AI_SUMMARY:
    case DatabaseFieldType.CHECKBOX:
      return null;

    default:
      return unreachable(field.type);
  }
};
